import type { AuthenticationStrategy, Host } from '@wix/sdk-types';
import { ACTIONS, DATA, LAYOUT } from '@wix/component-protocol/schema';
import { legacySystemComponentsActions } from '@wix/editor-system-components-legacy';
import type { ValueOf, CompRef } from '@wix/document-services-types';
import {
  PlatformLinksServiceAPI,
  ProvidersServiceAPI,
  DataBindingServiceAPI,
} from '@wix/editor-platform-feature-store';
import type { EditorPlatformPanoramaClientAPI } from '@wix/editor-platform-feature-store/internal';
import {
  ManifestComponentExtensionType,
  MetadataByExtensionType,
} from './stores';

import { IVirtualManifestBuilderProps } from './api';

import {
  EditorContextProvider,
  EditorReactComponent,
  InlineElement,
  EditorElement,
  ContainerLayout,
} from '@wix/ambassador-devcenter-v1-component-type-data/types';
import {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';
/**
 * TODO: add types
 */
export type DocumentServices = any;
export type HostAPI = any;

export type ManifestOverrides = Partial<EditorElement>;

export type ManifestTypes = EditorReactComponent | EditorContextProvider;

export interface IServiceTopology {
  scriptsLocationMap: Record<string, string>;
  [key: string]: undefined | boolean | string | Record<string, string>;
}

export interface IWixCLIIntegrationAPI {
  getSDKHostContext(
    props: { appDefinitionId?: string } & IVirtualManifestBuilderProps,
  ): {
    getHost: () => Host;
    getAuth: () => AuthenticationStrategy;
  };
  getSDKAuth: (props?: { appDefinitionId?: string }) => AuthenticationStrategy;
}

/**
 * Bridge interface subset for manifest services.
 * Contains only the services needed by manifest-services package.
 * This is a subset of the full IManifestServicesBridge from host-integration-apis.
 */
export interface IManifestServicesBridge {
  EditorPointerAPI: EditorPointerServiceAPI;
  LinkServiceAPI: PlatformLinksServiceAPI;
  ProvidersServiceAPI: ProvidersServiceAPI;
  DataBindingServiceAPI: DataBindingServiceAPI;
  EditorPlatformPanoramaClientAPI: EditorPlatformPanoramaClientAPI;
}

/**
 * Style-derived native action constants.
 * These actions are derived from style properties.
 */
export const StylesDerivedNativeActions = {
  globalColorsStyles: 'globalColorsStyles',
  textStyles: 'textStyles',
  borderStyles: 'borderStyles',
  cornerStyles: 'cornerStyles',
  fillStyles: 'fillStyles',
  shadowStyles: 'shadowStyles',
  spacingStyles: 'spacingStyles',
  strokeStyles: 'strokeStyles',
  filterStyles: 'filterStyles',
} as const;

export type StylesDerivedNativeActionNames =
  keyof typeof StylesDerivedNativeActions;

/**
 * Rich text derived native action constants.
 * These actions are specific to rich text editing.
 */
export const RichTextDerivedNativeActions = {
  richTextAlignment: 'richTextAlignment',
  richTextBold: 'richTextBold',
  richTextDirection: 'richTextDirection',
  richTextFontFamily: 'richTextFontFamily',
  richTextFontSize: 'richTextFontSize',
  richTextForeColor: 'richTextForeColor',
  richTextItalic: 'richTextItalic',
  richTextLink: 'richTextLink',
  richTextPanel: 'richTextPanel',
  richTextStrikethrough: 'richTextStrikethrough',
  richTextUnderline: 'richTextUnderline',
} as const;

/**
 * All derived native actions combining style and rich text actions.
 */
export const DerivedNativeActions = {
  animation: 'animation',
  pin: 'pin',
  help: 'help',
  stretch: 'stretch',
  opacity: 'opacity',
  effects: 'effects',
  presets: 'presets',
  link: 'link',
  elements: 'elements',
  interactions: 'interactions',
  changeMedia: 'changeMedia',
  editImage: 'editImage',
  cropImage: 'cropImage',
  adjustImage: 'adjustImage',
  filterImage: 'filterImage',
  editText: 'editText',
  editRichText: 'editRichText',
  plugins: 'plugins',
  designStates: 'designStates',
  ...StylesDerivedNativeActions,
  ...RichTextDerivedNativeActions,
} as const;

export type NativeActionsListType = typeof DerivedNativeActions &
  typeof ACTIONS.ACTION_NAME &
  typeof legacySystemComponentsActions;

/**
 * Complete list of all native actions combining:
 * - Component protocol actions
 * - Legacy system component actions
 * - Derived native actions
 */
export const NativeActionsList: NativeActionsListType = {
  ...ACTIONS.ACTION_NAME,
  ...legacySystemComponentsActions,
  ...DerivedNativeActions,
} as const;

/**
 * Mapping from action names to their corresponding data types.
 * Used to determine which data type an action operates on.
 */
export const ACTION_NAME_TO_DATA_TYPE: Partial<
  Record<
    keyof typeof NativeActionsList,
    (typeof DATA.DATA_TYPE)[keyof typeof DATA.DATA_TYPE]
  >
> = {
  [NativeActionsList.link]: DATA.DATA_TYPE.link,
  [NativeActionsList.editText]: DATA.DATA_TYPE.text,
  [NativeActionsList.editRichText]: DATA.DATA_TYPE.richText,
  [NativeActionsList.richTextAlignment]: DATA.DATA_TYPE.richText,
  [NativeActionsList.richTextBold]: DATA.DATA_TYPE.richText,
  [NativeActionsList.richTextDirection]: DATA.DATA_TYPE.richText,
  [NativeActionsList.richTextFontFamily]: DATA.DATA_TYPE.richText,
  [NativeActionsList.richTextFontSize]: DATA.DATA_TYPE.richText,
  [NativeActionsList.richTextForeColor]: DATA.DATA_TYPE.richText,
  [NativeActionsList.richTextItalic]: DATA.DATA_TYPE.richText,
  [NativeActionsList.richTextPanel]: DATA.DATA_TYPE.richText,
  [NativeActionsList.richTextStrikethrough]: DATA.DATA_TYPE.richText,
  [NativeActionsList.richTextUnderline]: DATA.DATA_TYPE.richText,
};

/**
 * Breakpoint type excluding the unknown breakpoint.
 */
export type Breakpoint = Exclude<
  ValueOf<typeof LAYOUT.BREAKPOINT>,
  typeof LAYOUT.BREAKPOINT.UNKNOWN_Breakpoint
>;

export interface ContainerLayoutTransitionConfig {
  containerLayout?: ContainerLayout;
  activeContainerLayout?: ContainerLayout;
}

/**
 * Action name type from component protocol.
 */
export type ActionName =
  (typeof ACTIONS.ACTION_NAME)[keyof typeof ACTIONS.ACTION_NAME];

/**
 * Definition descriptions for components.
 */
export type IDefinitionDescriptions = {
  cssCustomProperties: Record<string, { description: string }>;
  presets: Record<string, { description: string }>;
  description?: string;
  data: Record<string, { description: string }>;
};

/**
 * Represents an actionable element in the editor hierarchy.
 */
export interface ActionableElement {
  editorPointers: EditorPointer<CompRef>[];
  elements: ActionableElement[];
  displayName?: string;
  isActionable: boolean;
}

export interface IActionableElementsResult {
  elements: ActionableElement[];
}
